import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'
import { CHECKOUT_ENDPOINTS } from './checkout.endpoints'
import type {
  CancelOrderRequest,
  CancelOtpRequest,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutStatusResponse,
  OrderStatusResponse,
  PreviewOrderRequest,
  PreviewOrderResponse,
} from './checkout.types'

export const previewOrderService = async (payload: PreviewOrderRequest) => {
  const { data } = await apiClient.post<PreviewOrderResponse>(CHECKOUT_ENDPOINTS.PREVIEW_ORDER, payload)
  return data
}

export const usePreviewOrder = () => {
  return useMutation({
    mutationFn: previewOrderService,
  })
}
export const usePreviewOrderQuery = (payload: PreviewOrderRequest, enabled = true) => {
  return useQuery({
    queryKey: ['orderPreview', payload.customer_shipping_slug, payload.delivery_type],
    queryFn: () => previewOrderService(payload),
    enabled: !!payload.customer_shipping_slug && enabled,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}

export const checkoutService = async (payload: CheckoutRequest) => {
  const { data } = await apiClient.post<CheckoutResponse>(CHECKOUT_ENDPOINTS.CHECKOUT, payload)
  return data
}

export const useCheckoutMutation = () => {
  return useMutation({
    mutationFn: checkoutService,
  })
}

// Order Status & Cancellation
export const orderStatusService = async (sessionId: string) => {
  const { data } = await apiClient.get<OrderStatusResponse>(
    `${CHECKOUT_ENDPOINTS.ORDER_STATUS}?session_id=${sessionId}`,
  )
  return data
}

export const useOrderStatusQuery = (sessionId: string | null) => {
  return useQuery({
    queryKey: ['orderStatus', sessionId],
    queryFn: () => orderStatusService(sessionId!),
    enabled: !!sessionId,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 3000 : false), // Poll every 3s if pending
  })
}

export const sendCancelOtpService = async (payload: CancelOtpRequest) => {
  const { data } = await apiClient.post(CHECKOUT_ENDPOINTS.CANCEL_ORDER, payload)
  return data
}

export const useSendCancelOtpMutation = () => {
  return useMutation({
    mutationFn: sendCancelOtpService,
  })
}

export const confirmCancelOrderService = async (payload: CancelOrderRequest) => {
  const { data } = await apiClient.post(CHECKOUT_ENDPOINTS.CANCEL_ORDER_CONFIRM, payload)
  return data
}

export const useConfirmCancelOrderMutation = () => {
  return useMutation({
    mutationFn: confirmCancelOrderService,
  })
}

export const orderDetailsByNumberService = async (orderNumber: string) => {
  const { data } = await apiClient.get<OrderStatusResponse>(`${CHECKOUT_ENDPOINTS.ORDER_BY_NUMBER}${orderNumber}/`)
  return data
}

export const useOrderDetailsByNumberQuery = (orderNumber: string | undefined) => {
  return useQuery({
    queryKey: ['orderDetails', orderNumber],
    queryFn: () => orderDetailsByNumberService(orderNumber!),
    enabled: !!orderNumber,
  })
}

export const checkoutStatusService = async (orderId: string) => {
  const { data } = await apiClient.get<CheckoutStatusResponse>(`${CHECKOUT_ENDPOINTS.CHECKOUT_STATUS}${orderId}/`)
  return data
}

export const useCheckoutStatusQuery = (orderId: string | undefined) => {
  return useQuery({
    queryKey: ['checkoutStatus', orderId],
    queryFn: () => checkoutStatusService(orderId!),
    enabled: !!orderId,
    refetchInterval: (query) => (query.state.data?.order_status === 'pending' ? 3000 : false),
  })
}
